/* @refresh reset */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type CookieConsent = {
  necessary: boolean;
  analytics: boolean;
};

type CookieConsentContextType = {
  consent: CookieConsent | null;
  hasConsent: (category: keyof CookieConsent) => boolean;
  setConsent: (consent: CookieConsent) => void;
  showBanner: boolean;
  setShowBanner: (show: boolean) => void;
};

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

const CONSENT_STORAGE_KEY = 'cookie_consent';
const CONSENT_BANNER_SHOWN_KEY = 'cookie_consent_banner_shown';

export const CookieConsentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [consent, setConsentState] = useState<CookieConsent | null>(null);
  const [showBanner, setShowBannerState] = useState<boolean>(false);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    const bannerShown = localStorage.getItem(CONSENT_BANNER_SHOWN_KEY);

    if (stored) {
      try {
        const parsed = JSON.parse(stored) as CookieConsent;
        setConsentState(parsed);
        setShowBannerState(false);
      } catch {
        setShowBannerState(true);
      }
    } else {
      setShowBannerState(bannerShown !== 'true');
    }
  }, []);

  const setConsent = (newConsent: CookieConsent): void => {
    setConsentState(newConsent);
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(newConsent));
    localStorage.setItem(CONSENT_BANNER_SHOWN_KEY, 'true');
    setShowBannerState(false);
  };

  const setShowBanner = (show: boolean): void => {
    setShowBannerState(show);
    if (show) {
      localStorage.removeItem(CONSENT_BANNER_SHOWN_KEY);
    } else {
      localStorage.setItem(CONSENT_BANNER_SHOWN_KEY, 'true');
    }
  };

  const hasConsent = (category: keyof CookieConsent): boolean => {
    if (!consent) return false;
    if (category === 'necessary') return true;
    return consent[category] === true;
  };

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        hasConsent,
        setConsent,
        showBanner,
        setShowBanner,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = (): CookieConsentContextType => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return context;
};

